import {
  FieldTypes,
  IField,
} from "@/api/AppApi/services/builder/interfaces/interfaces";
import {
  DateServerFormats,
  convertToDate,
  getDate,
  getTime,
} from "@/utils/functions/date-time-functions";
import { FieldValues, UseFormReturn } from "react-hook-form";
const safeJSON = (json: string) => {
  let a: { en: string; ar: string };
  a = { en: "", ar: "" };
  try {
    a = JSON.parse(json);
  } catch (e) {}
  return a ?? { en: json, ar: json };
};

const leaf = (obj: any, path: string, value: any) => {
  const pList = path.split(".");
  const key = pList.pop() ?? "";
  const pointer = pList.reduce((accumulator, currentValue) => {
    if (accumulator[currentValue] === undefined) accumulator[currentValue] = {};
    return accumulator[currentValue];
  }, obj);
  pointer[key] = value;
  return obj;
};

export const renameFields = (
  OriginFields: IField[] | undefined,
  splitMultiLang = false,
) => {
  if (!OriginFields) return;
  let fields: IField[] = [];
  OriginFields.forEach((field) => {
    if (field.configurations.multi_lang && splitMultiLang) {
      const { en: enVal, ar: arVal } = safeJSON(field.value);
      const { key, title, hint, placeholder } = field.configurations;
      const { en: enTitle, ar: arTitle } = safeJSON(title);
      const { en: enHint, ar: arHint } = safeJSON(hint);
      const { en: enPlaceholder, ar: arPlaceholder } = safeJSON(placeholder!);
      fields.push({
        ...field,
        value: enVal,
        configurations: {
          ...field.configurations,
          key: key + ".en",
          title: enTitle,
          hint: enHint,
          placeholder: enPlaceholder,
        },
      });
      fields.push({
        ...field,
        value: arVal,
        configurations: {
          ...field.configurations,
          key: key + ".ar",
          title: arTitle,
          hint: arHint,
          placeholder: arPlaceholder,
        },
      });
    } else {
      fields.push(field);
    }
  });
  const values = fields?.map((field) => {
    if (!field?.configurations?.key?.includes(".")) return field;
    let fConfig = { ...field.configurations };
    fConfig.key = fConfig.key.replaceAll(".", "(");
    if (fConfig.parent_key)
      fConfig.parent_key = fConfig.parent_key.replaceAll(".", "(");
    return { ...field, configurations: fConfig };
  });
  return values;
};

export const defaultValues = (fields: IField[] | undefined) => {
  if (!fields || fields.length === 0) return;
  const values = fields?.reduce((acc, field) => {
    let fConfig = field?.configurations;
    switch (fConfig?.type) {
      case FieldTypes.phone:
      case FieldTypes.mobile:
        return leaf(acc, fConfig.key, {
          type: fConfig?.type,
          number: field.value ? field.value.number : "",
          dial: field.value ? field.value.dial_code_country : null,
        });
      case FieldTypes.date:
        return leaf(acc, fConfig.key, {
          type: fConfig?.type,
          value: field.value
            ? convertToDate(field.value, DateServerFormats.date)
            : null,
        });
      case FieldTypes.time:
        return leaf(acc, fConfig.key, {
          type: fConfig?.type,
          value: field.value
            ? convertToDate(field.value, DateServerFormats.time)
            : null,
        });
      case FieldTypes.date_time:
        return leaf(acc, fConfig.key, {
          type: fConfig?.type,
          value: field.value
            ? convertToDate(field.value, DateServerFormats.data_time)
            : null,
        });
      case FieldTypes.select:
        return leaf(
          acc,
          fConfig?.key,
          field.value ? field.value?.toString() : "-1",
        );
      case FieldTypes.checkbox:
      case FieldTypes.multi_file:
      case FieldTypes.multi_select:
        return leaf(acc, fConfig?.key, field.value ?? []);
      default:
        return leaf(acc, fConfig?.key, field.value ?? "");
    }
  }, {});
  return values;
};

export const adaptFieldsToPost = (data: any): any => {
  let adaptedFields = {};
  for (let key in data) {
    const keyToPost = key.replaceAll("(", ".");
    switch (data[key]?.type) {
      case FieldTypes.phone:
      case FieldTypes.mobile:
        adaptedFields = data[key].number
          ? leaf(adaptedFields, keyToPost, {
              number: data[key].number,
              country: data[key].dial?.iso2?.toUpperCase(),
            })
          : leaf(adaptedFields, keyToPost, null); //{ number: "", country: "" });
        break;
      case FieldTypes.date:
        leaf(adaptedFields, keyToPost, getDate(data[key].value));
        break;
      case FieldTypes.time:
        leaf(adaptedFields, keyToPost, getTime(data[key].value));
        break;
      case FieldTypes.date_time:
        leaf(
          adaptedFields,
          keyToPost,
          data[key].value
            ? `${getDate(data[key].value)} ${getTime(data[key].value)}`
            : null,
        );
        break;
      default:
        adaptedFields = leaf(adaptedFields, keyToPost, data[key]);
        break;
    }
  }
  return adaptedFields;
};
export const formErrors = (
  error: any,
  form: UseFormReturn<FieldValues, any>,
): string[] => {
  let notField_errs: string[] = [];
  const errData = error.response?.data;
  if (error.response?.status === 422) {
    const fieldNames = Object.keys(form.getValues()).map((name) =>
      name.replaceAll("(", "."),
    );
    for (let [key, value] of Object.entries(errData?.errors)) {
      if (fieldNames.includes(key)) {
        form.setError(key.replaceAll(".", "(") as any, {
          message: (value as string[])[0],
        });
      } else {
        notField_errs.push((value as string[])[0]);
      }
    }
  } else {
    notField_errs.push(errData?.message?.text);
  }
  return notField_errs;
};
export const validateFormController = ({
  form,
  fieldName,
  value,
  is_required,
  requiredMsg,
}: {
  form: UseFormReturn<FieldValues, any>;
  value: any;
  fieldName: string;
  is_required: boolean;
  requiredMsg: string;
}) => {
  if (is_required && !value) {
    form.setError(fieldName, { message: requiredMsg });
    return false;
  }
  form.clearErrors(fieldName);
  return true;
};
interface CreateCustomField {
  key: string;
  type: FieldTypes;
  title: string;
  is_required: boolean;
  value: any;
}
export const createCustomFields = (fields: CreateCustomField[]) => {
  const createField = (field: CreateCustomField): IField => {
    return {
      type: "field",
      configurations: {
        key: field.key,
        title: field.title,
        section: null,
        type: field.type,
        multi_lang: false,
        is_required: field.is_required,
        is_extra: false,
        hint: "",
        parent_key: null,
        options_url: null,
      },
      value: field.value,
      data: [],
    };
  };
  let mainFields: IField[] = [];
  for (let i = 0; i < fields.length; i++)
    mainFields.push(createField(fields[i]));
  return mainFields;
};
